import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { distance } from '../match/Vector2';
import { roleFamily } from './RoleProfiles';

export type FormationRelationshipType = 'COVER' | 'SUPPORT' | 'OVERLAP';

export interface FormationRelationship {
  type: FormationRelationshipType;
  fromPlayerId: string;
  toPlayerId: string;
}

export type TeamFormationRelationships = [FormationRelationship[], FormationRelationship[]];

export class FormationRelationships {
  build(players: readonly PlayerRuntimeState[]): TeamFormationRelationships {
    return [this.forSide(players.filter((player) => player.teamSide === 0)), this.forSide(players.filter((player) => player.teamSide === 1))];
  }

  private forSide(players: PlayerRuntimeState[]): FormationRelationship[] {
    const outfield = players.filter((player) => player.positionType !== 'GK');
    const relationships: FormationRelationship[] = [];
    const add = (type: FormationRelationshipType, from: PlayerRuntimeState | undefined, to: PlayerRuntimeState | undefined): void => {
      if (!from || !to || from.playerId === to.playerId) return;
      if (!relationships.some((item) => item.type === type && item.fromPlayerId === from.playerId && item.toPlayerId === to.playerId)) {
        relationships.push({ type, fromPlayerId: from.playerId, toPlayerId: to.playerId });
      }
    };

    for (const player of outfield) {
      const playerProgress = player.teamSide === 0 ? player.homePosition.x : 100 - player.homePosition.x;
      const deeper = outfield
        .filter((candidate) => candidate.playerId !== player.playerId
          && (candidate.teamSide === 0 ? candidate.homePosition.x : 100 - candidate.homePosition.x) < playerProgress)
        .sort((a, b) => distance(a.homePosition, player.homePosition) - distance(b.homePosition, player.homePosition)
          || a.playerId.localeCompare(b.playerId))[0];
      add('COVER', deeper, player);
    }

    const attackers = outfield.filter((player) => ['ATTACKING_MIDFIELDER', 'WINGER', 'STRIKER'].includes(roleFamily(player.role, player.positionType)));
    const midfielders = outfield.filter((player) => ['HOLDING_MIDFIELDER', 'CENTRAL_MIDFIELDER', 'ATTACKING_MIDFIELDER'].includes(roleFamily(player.role, player.positionType)));
    for (const attacker of attackers) {
      const support = [...midfielders]
        .sort((a, b) => distance(a.homePosition, attacker.homePosition) - distance(b.homePosition, attacker.homePosition)
          || a.playerId.localeCompare(b.playerId))[0];
      add('SUPPORT', support, attacker);
    }

    const wideDefenders = outfield.filter((player) => ['FULL_BACK', 'WING_BACK'].includes(roleFamily(player.role, player.positionType)));
    const wingers = outfield.filter((player) => roleFamily(player.role, player.positionType) === 'WINGER');
    for (const defender of wideDefenders) {
      const sameLane = [...wingers]
        .sort((a, b) => Math.abs(a.homePosition.y - defender.homePosition.y) - Math.abs(b.homePosition.y - defender.homePosition.y)
          || a.playerId.localeCompare(b.playerId))[0];
      add('OVERLAP', defender, sameLane);
    }

    return relationships.sort((a, b) => a.type.localeCompare(b.type)
      || a.fromPlayerId.localeCompare(b.fromPlayerId) || a.toPlayerId.localeCompare(b.toPlayerId));
  }
}
